import React from "react";
import { ToggleCheckbox } from "../../../components/tailwind";
import { Text } from "../../../components/Text";
import { findConnectedChannelsForSection } from "../../copy-assistant/utils";
import { useGetCustomerQuery } from "../../../api/customerApi";
import { useGetDocumentStructures } from "../../../planner/document-structure/manage/customhooks";

export type FieldsetFormListItem = {
  name: string;
  id?: number;
  origin: "data" | "section" | "fieldset";
};

type Props = {
  testIdPrefix: string;
  items: FieldsetFormListItem[];
  isItemSelected: (item: FieldsetFormListItem) => boolean;
  onToggle: (item: FieldsetFormListItem, next: boolean) => void;
  maxHeight: number;
  emptyMessage?: string;
  header?: React.ReactNode;
};

const FieldsetFormListInner: React.FC<Props> = ({
  testIdPrefix,
  items,
  isItemSelected,
  onToggle,
  maxHeight,
  emptyMessage,
  header,
}) => {
  const { data: customer } = useGetCustomerQuery();
  const { data: structures } = useGetDocumentStructures();

  const createDisplayName = (item: FieldsetFormListItem): React.ReactNode => {
    if (item.origin === "section") {
      let additionalInfo = "(Section)";
      if (item?.id && customer && structures) {
        const channelDisplayNames = findConnectedChannelsForSection(
          item.id,
          customer,
          structures
        )
          .map(({ name }) => name)
          .join(", ");
        if (channelDisplayNames) {
          additionalInfo = `(Section connected to ${channelDisplayNames})`;
        }
      }
      return (
        <>
          <b>{item.name}</b>{" "}
          <Text size="tiny" color="grey" inline compact>
            {additionalInfo}
          </Text>
        </>
      );
    }
    return item.name;
  };

  return (
    <div
      data-testid={`${testIdPrefix}-list`}
      className="tw-overflow-auto"
      style={{
        height: `${maxHeight}px`,
        overflowX: "hidden",
        scrollbarGutter: "stable",
      }}
    >
      {header && (
        <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-white tw-pt-3 tw-pb-2">
          {header}
        </div>
      )}
      <ul className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-100">
        {items.length === 0 && (
          <li
            className="tw-px-3 tw-py-3 tw-text-sm tw-text-gray-500"
            data-testid={`${testIdPrefix}-empty`}
          >
            {emptyMessage ?? "No items"}
          </li>
        )}
        {items.map((item) => {
          const checkboxId = `${testIdPrefix}-toggle-${item.name}`;
          const checked = isItemSelected(item);
          return (
            <li
              key={item?.id ?? item.name}
              data-testid={`${testIdPrefix}-list-item-${item.name}`}
              className="tw-flex tw-items-center tw-gap-2 tw-py-2 tw-pl-1 tw-pr-2"
            >
              <label
                htmlFor={checkboxId}
                className="tw-flex-1 tw-min-w-0 tw-text-sm tw-cursor-pointer"
              >
                {createDisplayName(item)}
              </label>
              <ToggleCheckbox
                checkboxId={checkboxId}
                checked={checked}
                setChecked={(next): void => onToggle(item, next)}
                data-testid={`${testIdPrefix}-toggle-${item.name}`}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const FieldsetFormList = React.memo(FieldsetFormListInner);
FieldsetFormList.displayName = "FieldsetFormList";
